import { PAGE } from '@/routes';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InfoIcon from '@/assets/icons/info.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import vaultIcon from '@/assets/icons/vault.svg';
import { Button } from '@/components/ui/button';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccount } from '@/services/types/bank-accounts.types';
import ExplanatoryModal from '@/components/molecules/ExplanatoryModal';

import baExpanatoryImg from '@/assets/images/ba-explanatory-1.png';
import baExpanatoryImg2 from '@/assets/images/ba-explanatory-2.png';

const steps = [
  {
    title: 'What Is a Bank Account?',
    message:
      'Bank accounts act as the primary source for payments and deposits, seamlessly integrating with your budget.',
    img: baExpanatoryImg
  },
  {
    title: 'Tracking Invoices in Your Bank Account',
    message:
      'All invoices are linked to bank accounts, enabling tracking of cash inflows and outflows while maintaining account balances.',
    img: baExpanatoryImg2
  }
];

const BankAccountsPage = () => {
  const [bankAccountMode, setBankAccountMode] = useState('percentage');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false);
  const [totalInitialAmount, setTotalInitialAmount] = useState(0);
  const [totalRemainingBalance, setTotalRemainingBalance] = useState(0);
  const [percentageRemaining, setPercentageRemaining] = useState(0);
  const [isExplanatoryOpen, setIsExplanatoryOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { budgetId = '' } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Sets breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budgets',
          path: PAGE.BUDGETS.path
        },
        {
          title: 'Bank Accounts Overview',
          path: PAGE.BANK_ACCOUNTS.path
        }
      ])
    );

    fetchBankAccounts();
  }, []);

  useEffect(() => {
    // Calculate sums and percentages when bank accounts data changes
    if (bankAccounts && bankAccounts.length > 0) {
      const totalInitial = bankAccounts.reduce((sum, account) => sum + (account.initialAmount || 0), 0);
      const totalRemaining = bankAccounts.reduce((sum, account) => sum + (account.remainingBalance || 0), 0);

      setTotalInitialAmount(totalInitial);
      setTotalRemainingBalance(totalRemaining);

      // Calculate percentage remaining - handle division by zero
      const percentage = totalInitial > 0 ? (totalRemaining / totalInitial) * 100 : 0;
      setPercentageRemaining(parseFloat(percentage.toFixed(2)));
    }
  }, [bankAccounts]);

  const fetchBankAccounts = async () => {
    setIsLoadingBankAccounts(true);
    try {
      const response = await bankAccountService.getBankAccounts(budgetId);
      setBankAccounts(response?.data);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    } finally {
      setIsLoadingBankAccounts(false);
    }
  };

  const handleBankAccountClick = (bankAccountId: string) => {
    navigate(PAGE?.BANK_ACCOUNTS?.children?.DETAIL.replace(':bankAccountId', bankAccountId));
  };

  // Calculate percentage change from initial amount
  const calculatePercentageChange = () => {
    if (totalInitialAmount === 0) return 0;
    const change = ((totalRemainingBalance - totalInitialAmount) / totalInitialAmount) * 100;
    return parseFloat(change.toFixed(2));
  };

  // Format currency as euros
  const formatEuro = (amount: any) => {
    return `€${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="w-full flex items-stretch justify-between gap-4 h-full">
      <div className="flex flex-col items-stretch flex-1 gap-5">
        <div className="border bg-white rounded-[16px] p-4">
          <header className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-semibold">Bank Account</h2>
            <button
              title="info"
              onClick={() => {
                setCurrentStep(0);
                setIsExplanatoryOpen(true);
              }}
            >
              <img src={InfoIcon} alt="info-icon" />
            </button>
          </header>
          <div className="flex items-start justify-between mt-5">
            <div>
              <p className="text-primary font-medium text-sm leading-[180%]">Balance</p>
              {bankAccountMode === 'percentage' ? (
                <p className="text-primary font-bold text-[1.75rem] leading-[100%] mb-[0.5rem]">
                  {percentageRemaining}%
                </p>
              ) : (
                <p className="text-primary font-bold text-[1.75rem] leading-[100%] mb-[0.5rem]">
                  {formatEuro(totalRemainingBalance)}
                </p>
              )}
              <span className="text-secondary font-medium text-sm">
                {calculatePercentageChange() >= 0 ? '+' : ''}
                {calculatePercentageChange()}% from Initial amount
              </span>
            </div>
            <div>
              <Tabs defaultValue="percentage" value={bankAccountMode} onValueChange={setBankAccountMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-stretch border bg-white rounded-[16px] p-4">
          <header>
            <h4 className="flex items-center gap-2">
              <img src={invoiceIcon} alt="invoice-icon" />
              <span className="font-bold">Recent Invoice</span>
            </h4>
          </header>
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <img className="w-[40px] h-[40px]" src={noInvoiceIcon} alt="no-invoice-icon" />
              <div className="flex flex-col items-start justify-center">
                <p className="font-medium leading-[140%]">There are no Invoice to show</p>
                <span className="text-secondary text-sm leading-[125%]">
                  Please create a new invoice to allocate budget!
                </span>
              </div>
            </div>
            <Button variant={'primary-reverse'} className="rounded-full px-6">
              Create an Invoice
            </Button>
          </div>
        </div>
      </div>
      <aside className="w-[360px] border bg-white rounded-[16px] p-4">
        <p className="font-bold mb-4">All Bank Accounts</p>
        {isLoadingBankAccounts ? (
          <p className="text-sm text-secondary text-center py-4">Loading...</p>
        ) : (
          <div className="flex flex-col gap-4">
            {bankAccounts && bankAccounts?.length > 0 ? (
              bankAccounts.map((bankAccount: any) => (
                <button
                  onClick={() => handleBankAccountClick(bankAccount._id)}
                  key={bankAccount._id}
                  className="flex items-center border rounded-[8px] min-h-[88px] hover:border-lightPurple-300 transition-all duration-300"
                >
                  <div className="px-5">
                    <img className="w-[40px] h-[40px]" src={vaultIcon} alt="vault-icon" />
                  </div>
                  <div className="border-l p-6 py-4 text-left">
                    <p className="font-bold leading-[110%] capitalize">{bankAccount.accountName || '-'}</p>
                    <span className="text-xs text-secondary leading-[100%]">
                      Remaining Balance €{bankAccount?.remainingBalance?.toLocaleString() || '-'}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <p className="text-sm text-secondary text-center py-4">No bank accounts found</p>
            )}
          </div>
        )}
      </aside>

      {/* Explanatory Modal */}
      {isExplanatoryOpen && (
        <ExplanatoryModal
          isOpen={isExplanatoryOpen}
          steps={steps}
          currentStep={currentStep}
          buttonText="Next"
          onButtonClick={() => {
            setCurrentStep(currentStep + 1);
          }}
          onClose={() => {
            setIsExplanatoryOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default BankAccountsPage;
